import React from 'react';
import PropTypes from 'prop-types';
import { candidateCellColumn } from './CandidateCell';
import { waitingOnColumn } from './WaitingOnCell';
import { waitingColumn, rowAccentForWaitingDays } from './WaitingCell';
import RowActionsCell from './RowActionsCell';
import SelectableTabTable from './SelectableTabTable';
import { candidateShape } from './PipelineDataTable';
import TabEmptyState from './TabEmptyState';
import { registerTab } from './tabRegistry';

// Secondary screening flow — "First contact" tab (data-stage="contact" in
// the screening-pipeline-v3 mockup). Also covers the underlying "profile"
// stage the mockup groups into this same tab (STAGE_TABS:
// { contact: ['contact', 'profile'] }) — a candidate row here may be
// waiting on a first reply *or* on a details form, which is what
// "Waiting On" disambiguates (see WaitingOnCell.jsx).
//
// Row layout (checkbox / Candidate / Waiting On / Waiting / actions) and
// the colored left-edge urgency accent are adapted from a reference design
// screenshot. `waiting_days` / `waiting_status_text` / `waiting_on_type` /
// `waiting_on_items` remain best-guess field names — no confirmed API
// contract for the secondary flow's candidate payload exists yet.
const FirstContactTab = ({
  candidates,
  isLoading,
  page,
  pageSize,
  totalPages,
  totalItems,
  onPageChange,
  onPageSizeChange,
  opportunityId,
  assignmentId,
  requirementLocationId,
  stateMachineKey,
  manualActions,
  onActionSuccess,
  onViewProfile,
}) => {
  const resolvedColumns = [
    candidateCellColumn,
    waitingOnColumn,
    waitingColumn,
    {
      key: 'row_actions',
      header: '',
      className: 'text-sm',
      render: (_v, row) => (
        <RowActionsCell
          candidateId={row.candidate_id}
          mappingId={row.id}
          status="contact"
          opportunityId={opportunityId}
          assignmentId={assignmentId}
          requirementLocationId={requirementLocationId}
          stateMachineKey={stateMachineKey}
          manualActions={manualActions}
          onActionSuccess={onActionSuccess}
          onViewProfile={onViewProfile}
        />
      ),
    },
  ];

  return (
    <SelectableTabTable
      columns={resolvedColumns}
      candidates={candidates}
      isLoading={isLoading}
      page={page}
      pageSize={pageSize}
      totalPages={totalPages}
      totalItems={totalItems}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      opportunityId={opportunityId}
      stateMachineKey={stateMachineKey}
      manualActions={manualActions}
      onActionSuccess={onActionSuccess}
      getRowAccentClassName={rowAccentForWaitingDays}
      emptyState={
        <TabEmptyState description="No candidates are waiting on first contact." />
      }
    />
  );
};

FirstContactTab.propTypes = {
  candidates: PropTypes.arrayOf(candidateShape),
  isLoading: PropTypes.bool,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  totalPages: PropTypes.number,
  totalItems: PropTypes.number,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  opportunityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  assignmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  requirementLocationId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  stateMachineKey: PropTypes.string,
  manualActions: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string, label: PropTypes.string })
  ),
  onActionSuccess: PropTypes.func,
  onViewProfile: PropTypes.func,
};

FirstContactTab.defaultProps = {
  candidates: [],
  isLoading: false,
  page: 1,
  pageSize: 10,
  totalPages: 1,
  totalItems: 0,
  onPageChange: () => {},
  onPageSizeChange: () => {},
  opportunityId: undefined,
  assignmentId: undefined,
  requirementLocationId: undefined,
  stateMachineKey: 'secondary',
  manualActions: [],
  onActionSuccess: () => {},
  onViewProfile: () => {},
};

registerTab({
  stateMachineKey: 'secondary',
  state: 'contact',
  component: FirstContactTab,
});

export default FirstContactTab;
